import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Zap, Clock3, MessageCircleQuestion, FileSearch, Wrench, ClipboardList, FolderCheck } from "lucide-react";
import { MASCOT_URL } from "../components/flux/Decor";
import { useAuth, API } from "../context/AuthContext";
import MatrixNav, { triggerPanicRedirect } from "../components/matrix/MatrixNav";
import CaseBar from "../components/matrix/CaseBar";
import IngestPanel from "../components/matrix/IngestPanel";
import FilesList from "../components/matrix/FilesList";
import Timeline from "../components/matrix/Timeline";
import Insights from "../components/matrix/Insights";
import AskFlux from "../components/matrix/AskFlux";
import IntelPanel from "../components/matrix/IntelPanel";
import Toolbox from "../components/matrix/Toolbox";
import IntakePanel from "../components/matrix/IntakePanel";
import CoverageBar from "../components/matrix/CoverageBar";
import RecapPanel from "../components/matrix/RecapPanel";
import SmsPanel from "../components/matrix/SmsPanel";
import PublicPanel from "../components/matrix/PublicPanel";
import CollabDialog from "../components/matrix/CollabDialog";
import Paywall from "../components/matrix/Paywall";
import DocumentPortal from "../components/portal/DocumentPortal";

const display = "font-display";

const MODES = [
  { key: "legal", label: "⚖️ Legal Pleadings" },
  { key: "safety", label: "🛡️ Safety Shield Log" },
  { key: "family", label: "👶 Family Legacy" },
];

const TRACK_STATS = [
  { key: "support", label: "Support Track", accent: "#60a5fa" },
  { key: "safety", label: "Safety Incidents", accent: "#fb7185" },
  { key: "family", label: "Milestone Events", accent: "#34d399" },
  { key: "procedural", label: "Procedural Setup", accent: "#a78bfa" },
];


export default function Matrix() {
  const { user, setUser, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mode, setMode] = useState("legal");
  const [view, setView] = useState("timeline");
  const [cases, setCases] = useState([]);
  const [activeCase, setActiveCase] = useState(null);
  const [events, setEvents] = useState([]);
  const [files, setFiles] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [theme, setTheme] = useState(() => localStorage.getItem("flux_theme") || "light");
  const [collabCase, setCollabCase] = useState(null);
  const pollingRef = useRef(false);

  const toggleTheme = () => {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    localStorage.setItem("flux_theme", next);
  };

  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
  }, [authLoading, user, navigate]);

  const fetchCases = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cases`);
      setCases(res.data);
      return res.data;
    } catch (err) {
      console.error("Failed to load cases:", err);
      return [];
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchCases().then((list) => {
      if (list.length === 0) return;
      const stored = localStorage.getItem("flux_active_case");
      const valid = list.find((c) => c.case_id === stored);
      setActiveCase(valid ? stored : list[0].case_id);
    });
  }, [user, fetchCases]);

  const [vaultToken, setVaultToken] = useState(null);
  const vaultRef = useRef(null);
  vaultRef.current = vaultToken;

  const fetchEvents = useCallback(async (caseId) => {
    if (!caseId) return;
    try {
      const headers = vaultRef.current ? { "X-Vault-Token": vaultRef.current } : {};
      const res = await axios.get(`${API}/events`, { params: { case_id: caseId }, headers });
      setEvents(res.data);
    } catch (err) {
      console.error("Failed to load events:", err);
    } finally {
      setLoadingEvents(false);
    }
  }, []);

  const handleVaultToken = useCallback((tok) => {
    vaultRef.current = tok;
    setVaultToken(tok);
  }, []);

  useEffect(() => {
    if (activeCase) fetchEvents(activeCase);
  }, [vaultToken, activeCase, fetchEvents]);

  const fetchFiles = useCallback(async (caseId) => {
    if (!caseId) return [];
    try {
      const res = await axios.get(`${API}/files`, { params: { case_id: caseId } });
      setFiles(res.data);
      return res.data;
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    if (!activeCase) return;
    localStorage.setItem("flux_active_case", activeCase);
    setLoadingEvents(true);
    fetchEvents(activeCase);
    fetchFiles(activeCase);
  }, [activeCase, fetchEvents, fetchFiles]);

  // Poll files while any are processing
  useEffect(() => {
    if (!files.some((f) => f.status === "processing")) return;
    const interval = setInterval(async () => {
      const updated = await fetchFiles(activeCase);
      const stillProcessing = updated.some((f) => f.status === "processing");
      const justDone = updated.filter((f) => f.status === "done" && files.find((old) => old.file_id === f.file_id && old.status === "processing"));
      if (justDone.length > 0) {
        fetchEvents(activeCase);
        fetchCases();
        justDone.forEach((f) => f.duplicate_of
          ? toast.info(`"${f.original_name}" is identical to a file already in this case — linked as a duplicate.`)
          : toast.success(`"${f.stored_name}" processed — ${f.event_count} events added for review!`));
      }
      const justFailed = updated.filter((f) => f.status === "failed" && files.find((old) => old.file_id === f.file_id && old.status === "processing"));
      justFailed.forEach((f) => toast.error(`Flux couldn't process "${f.original_name}": ${f.error || "unknown error"}`));
      if (!stillProcessing) clearInterval(interval);
    }, 4000);
    return () => clearInterval(interval);
  }, [files, activeCase, fetchFiles, fetchEvents, fetchCases]);

  // Payment return polling
  useEffect(() => {
    const sessionId = searchParams.get("payment_session_id");
    if (!sessionId || pollingRef.current) return;
    pollingRef.current = true;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API}/billing/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setUser(res.data.user);
          toast.success("Capacitor recharged! Your Weekly Pass is active. ⚡");
          setSearchParams({});
          return;
        }
        if (res.data.status === "expired") {
          toast.error("Payment session expired. Please try again.");
          setSearchParams({});
          return;
        }
      } catch (err) {
        console.error("Payment status check failed:", err);
      }
      if (attempts < 6) setTimeout(poll, 2000);
      else {
        toast.error("Payment status check timed out — refresh in a moment.");
        setSearchParams({});
      }
    };
    poll();
  }, [searchParams, setSearchParams, setUser]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") triggerPanicRedirect();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const refreshPortal = useCallback(() => {
    fetchEvents(activeCase);
    fetchFiles(activeCase);
  }, [activeCase, fetchEvents, fetchFiles]);

  const handleExtracted = (newEvents) => {
    setEvents((prev) => [...prev, ...newEvents].sort((a, b) => a.date.localeCompare(b.date)));
    fetchCases();
  };

  const handleEventAdded = (ev) => {
    setEvents((prev) => [...prev, ev].sort((a, b) => a.date.localeCompare(b.date)));
    fetchCases();
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/events/${id}`);
      setEvents((prev) => prev.filter((e) => e.id !== id));
      fetchCases();
      toast.success("Event erased from the timeline.");
    } catch {
      toast.error("Couldn't erase that event.");
    }
  };

  const handleClearAll = async () => {
    try {
      await axios.delete(`${API}/events`, { params: { case_id: activeCase } });
      setEvents([]);
      fetchCases();
      toast.success("Timeline wiped clean. Fresh start!");
    } catch {
      toast.error("Couldn't wipe the timeline.");
    }
  };

  const handleCreateCase = async (name) => {
    try {
      const res = await axios.post(`${API}/cases`, { name });
      setCases((prev) => [...prev, res.data]);
      setActiveCase(res.data.case_id);
      toast.success(`Timeline "${name}" created!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create timeline.");
    }
  };

  const handleRenameCase = async (caseId, name) => {
    try {
      await axios.patch(`${API}/cases/${caseId}`, { name });
      setCases((prev) => prev.map((c) => (c.case_id === caseId ? { ...c, name } : c)));
      toast.success("Timeline renamed.");
    } catch {
      toast.error("Couldn't rename timeline.");
    }
  };

  const handleDeleteCase = async (caseId) => {
    if (!window.confirm("Delete this timeline and ALL its events, files, and insights? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/cases/${caseId}`);
      const remaining = cases.filter((c) => c.case_id !== caseId);
      setCases(remaining);
      if (remaining.length > 0) setActiveCase(remaining[0].case_id);
      toast.success("Timeline deleted.");
    } catch {
      toast.error("Couldn't delete timeline.");
    }
  };

  const handleDedupe = async () => {
    try {
      const res = await axios.post(`${API}/events/dedupe`, null, { params: { case_id: activeCase } });
      if (res.data.removed > 0) {
        fetchEvents(activeCase);
        fetchCases();
        toast.success(`Flux removed ${res.data.removed} duplicate event${res.data.removed === 1 ? "" : "s"}!`);
      } else {
        toast.success("No duplicates found — your timeline is clean!");
      }
    } catch {
      toast.error("Couldn't run duplicate cleanup.");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-[#0f1729] flex flex-col items-center justify-center gap-4">
        <img src={MASCOT_URL} alt="" className="w-24 h-24 object-contain animate-float" />
        <p className="text-[#f59e0b] font-mono text-xs animate-blink">Warming up the capacitor...</p>
      </div>
    );
  }

  const hasAccess = user.access.has_access;
  const activeCaseObj = cases.find((c) => c.case_id === activeCase);
  const activeCaseName = activeCaseObj?.name || "Main Timeline";
  const myRole = activeCaseObj?.my_role || "owner";
  const canEdit = myRole === "owner" || myRole === "editor";

  return (
    <div className={`bg-[#0f1729] text-gray-100 min-h-screen flex flex-col ${theme === "light" ? "flux-light" : ""}`} data-testid="matrix-page">
      <MatrixNav user={user} onLogout={handleLogout} theme={theme} onToggleTheme={toggleTheme} />

      <header className="max-w-6xl mx-auto w-full px-4 sm:px-6 pt-6 sm:pt-8 pb-4">
        <div className="bg-gradient-to-b from-[#131f3a] to-[#0f1729] border border-white/5 rounded-3xl p-5 sm:p-8 shadow-2xl relative overflow-hidden">
          <div className="absolute inset-0 opacity-5 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:3rem_3rem]" />
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-4 flex justify-center relative">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-[#f59e0b]/20 blur-2xl animate-pulse-glow" />
                <img src={MASCOT_URL} alt="Professor Flux" data-testid="matrix-mascot-img" className="relative w-28 h-28 sm:w-40 sm:h-40 object-contain animate-float" />
              </div>
              <div className={`absolute -top-2 right-2 bg-[#f59e0b] text-gray-950 text-[10px] font-bold px-2 py-0.5 rounded-md shadow transform rotate-6 ${display}`}>
                HI, {user.name ? user.name.split(" ")[0].toUpperCase() : "FRIEND"}!
              </div>
            </div>
            <div className="lg:col-span-8 text-center lg:text-left space-y-4">
              <h1 className={`${display} font-bold text-2xl md:text-3xl text-white tracking-tight uppercase`}>Unscramble Your Chronology Matrix</h1>
              <p className="text-gray-300 text-sm max-w-xl leading-relaxed mx-auto lg:mx-0">
                Paste text, drop in PDFs, images, audio, video, or links. Professor Flux reads, watches, listens — and structures parallel tracks automatically.
              </p>
              <div className="bg-gray-950 p-1.5 rounded-xl border border-white/5 inline-flex flex-wrap justify-center gap-1">
                {MODES.map((m) => (
                  <button
                    key={m.key}
                    onClick={() => setMode(m.key)}
                    data-testid={`mode-tab-${m.key}`}
                    className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors duration-200 ${mode === m.key ? "bg-[#f59e0b] text-gray-950 shadow-md" : "text-gray-300 hover:text-white"}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      <CaseBar
        cases={cases}
        activeCase={activeCase}
        onSelect={setActiveCase}
        onCreate={handleCreateCase}
        onRename={handleRenameCase}
        onDelete={handleDeleteCase}
        onManageCollabs={(id) => setCollabCase(id)}
      />

      {hasAccess && <CoverageBar caseId={activeCase} />}

      <main className="max-w-6xl mx-auto w-full px-6 pt-4 grid grid-cols-1 xl:grid-cols-12 gap-6 items-start pb-24 flex-1">
        <section className="xl:col-span-5 space-y-6">
          {!hasAccess ? (
            <Paywall />
          ) : canEdit ? (
            <IngestPanel mode={mode} caseId={activeCase} onExtracted={handleExtracted} onFileQueued={(f) => setFiles((prev) => [f, ...prev])} />
          ) : (
            <div className="bg-[#131f3a] border border-white/5 p-5 rounded-2xl shadow-xl text-center space-y-1" data-testid="viewer-notice">
              <p className="text-xs font-bold text-gray-300 uppercase tracking-wide">View-Only Access</p>
              <p className="text-[11px] text-gray-400">You've been invited to view this timeline. Ask the owner for editor access to add records.</p>
            </div>
          )}
          <FilesList files={files} onDeleted={(id) => setFiles((prev) => prev.filter((f) => f.file_id !== id))} />
          {myRole === "owner" && (
            <PublicPanel
              caseData={activeCaseObj}
              onEventAdded={handleEventAdded}
              onCaseUpdated={(updated) => setCases((prev) => prev.map((c) => (c.case_id === updated.case_id ? updated : c)))}
            />
          )}
          <SmsPanel />
          <RecapPanel />
          <div className="bg-[#131f3a] border border-white/5 p-5 rounded-2xl shadow-xl space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-300">Matrix Status</h4>
            <div className="grid grid-cols-2 gap-3 text-center">
              {TRACK_STATS.map((t) => (
                <div key={t.key} className="bg-gray-950 p-3 rounded-xl border border-white/5" data-testid={`track-stat-${t.key}`}>
                  <div className="text-[10px] font-mono text-gray-400 uppercase tracking-wide">{t.label}</div>
                  <div className={`${display} font-bold text-xl mt-1`} style={{ color: t.accent }}>
                    {events.filter((e) => e.tracks.includes(t.key)).length}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="xl:col-span-7 space-y-4">
          <div className="bg-gray-950 p-1 rounded-xl border border-white/5 inline-flex flex-wrap gap-1">
            <button
              onClick={() => setView("timeline")}
              data-testid="view-tab-timeline"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-colors duration-200 ${view === "timeline" ? "bg-[#f59e0b] text-gray-950" : "text-gray-300 hover:text-white"}`}
            >
              <Clock3 className="w-3.5 h-3.5" /> Timeline
            </button>
            <button
              onClick={() => setView("documents")}
              data-testid="view-tab-documents"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-colors duration-200 ${view === "documents" ? "bg-orange-400 text-gray-950" : "text-gray-300 hover:text-white"}`}
            >
              <FolderCheck className="w-3.5 h-3.5" /> Documents
            </button>
            <button
              onClick={() => setView("insights")}
              data-testid="view-tab-insights"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-colors duration-200 ${view === "insights" ? "bg-[#2dd4bf] text-gray-950" : "text-gray-300 hover:text-white"}`}
            >
              <Zap className="w-3.5 h-3.5" /> Flux Insights
            </button>
            <button
              onClick={() => setView("intel")}
              data-testid="view-tab-intel"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-colors duration-200 ${view === "intel" ? "bg-rose-400 text-gray-950" : "text-gray-300 hover:text-white"}`}
            >
              <FileSearch className="w-3.5 h-3.5" /> Case Intel
            </button>
            <button
              onClick={() => setView("intake")}
              data-testid="view-tab-intake"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-colors duration-200 ${view === "intake" ? "bg-sky-400 text-gray-950" : "text-gray-300 hover:text-white"}`}
            >
              <ClipboardList className="w-3.5 h-3.5" /> Data Registry
            </button>
            <button
              onClick={() => setView("toolbox")}
              data-testid="view-tab-toolbox"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-colors duration-200 ${view === "toolbox" ? "bg-emerald-400 text-gray-950" : "text-gray-300 hover:text-white"}`}
            >
              <Wrench className="w-3.5 h-3.5" /> Toolbox
            </button>
            <button
              onClick={() => setView("ask")}
              data-testid="view-tab-ask"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-colors duration-200 ${view === "ask" ? "bg-[#a78bfa] text-gray-950" : "text-gray-300 hover:text-white"}`}
            >
              <MessageCircleQuestion className="w-3.5 h-3.5" /> Ask Flux
            </button>
          </div>

          {view === "timeline" ? (
            <Timeline events={events} caseId={activeCase} loadingEvents={loadingEvents} onDelete={handleDelete} onClearAll={handleClearAll} onDedupe={handleDedupe} onEventAdded={handleEventAdded} canEdit={canEdit} isOwner={myRole === "owner"} vaultToken={vaultToken} onVaultToken={handleVaultToken} onRefresh={() => fetchEvents(activeCase)} />
          ) : view === "documents" ? (
            <DocumentPortal caseId={activeCase} canEdit={canEdit} onChanged={refreshPortal} />
          ) : view === "insights" ? (
            <Insights eventCount={events.length} caseId={activeCase} />
          ) : view === "intel" ? (
            <IntelPanel caseId={activeCase} canEdit={canEdit} />
          ) : view === "intake" ? (
            <IntakePanel caseId={activeCase} canEdit={canEdit} />
          ) : view === "toolbox" ? (
            <Toolbox caseId={activeCase} />
          ) : (
            <AskFlux caseId={activeCase} caseName={activeCaseName} />
          )}
        </section>
      </main>
      {collabCase && (
        <CollabDialog caseId={collabCase} caseName={cases.find((c) => c.case_id === collabCase)?.name || ""} onClose={() => setCollabCase(null)} />
      )}
    </div>
  );
}
